import _ from 'lodash';
import React, { useEffect, useState, useRef } from 'react';
import { toast } from "react-toastify";
import { api, dateHelpers, constants, helpers } from '../utils';
import { ServiceThreadSpinner, VendorInvoiceGrid, VendorTypeahead } from './';
import { useLocation } from 'react-router-dom';
import { DesktopContainer } from '../layouts';
import { DebounceInput } from 'react-debounce-input';
import { Col, Row, Button } from "reactstrap";
import { useNavigate } from 'react-router-dom';

export default function VendorInvoiceSummary({ vendorId, vendorName }) {
   const location = useLocation();
   const navigate = useNavigate();
   const firstRender = useRef(true);
   const [data, setData] = useState([]);
   const [page, setPage] = useState({ number: 1, size: 15, allowAll: false, callback: val => onPage(val), sizeCallback: null })
   const [searchDates, setSearchDates] = useState({ startDate: dateHelpers.getLookbackByDays(120), endDate: dateHelpers.currentDate(), vendorId: null })
   const [sort, setSort] = useState({ field: "", direction: "asc", callback: onSort });
   const [totalPages, setTotalPages] = useState(0);
   const [vendor, setVendor] = useState(null);
   const [working, setWorking] = useState(false);

   // eslint-disable-next-line react-hooks/exhaustive-deps
   useEffect(refreshData, [page])

   useEffect(() => {
      setPage({ ...page, number: 1 })
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [vendorId])

   useEffect(() => {
      if (location.state && location.state.type === "showSearch") {
         if (location.state.vendor !== vendor) setVendor(location.state.vendor);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [location])

   useEffect(() => {
      if (location && location.state && location.state.type === "showSearch") {
         if (vendor) {
            if (firstRender) {
               firstRender.current = false;
               Go();
            }
         }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [vendor])

   const exportExcel = () => {
      let payloadParam = location.state && location?.state?.type === "showSearch" ? { // payload param is for our full screen vendor search page only
         startDate: dateHelpers.formatDateForServer(searchDates.startDate),
         endDate: dateHelpers.formatDateForServer(searchDates.endDate),
         pageNumber: 0,
         pageSize: 1000, //this should suffice as a limit but can be changed 
         sortField: sort.field,
         sortDirection: sort.direction,
         vendorId: vendor.value
      } : null;

      const payload = {
         startDate: dateHelpers.formatDateForServer(dateHelpers.getLookbackByDays(120)),
         endDate: dateHelpers.formatDateForServer(dateHelpers.currentDate()),
         pageNumber: page.number,
         pageSize: page.size,
         sortField: sort.field,
         sortDirection: sort.direction,
         vendorId: vendorId
      };
      api
         .postGetBlob(`Purchasing/ExportInvoiceSummary`, payloadParam ? payloadParam : payload)
         .then(r => {
            if (r.data.success === false) {
               if (_.isString(r?.data?.message)) {
                  toast.error(r?.data?.message);
               }
               return;
            }
            if (r.data) {
               const XLSX_FILENAME = vendorId ? `${vendorId}_InvoiceSummary.xlsx` : `InvoiceSummary.xlsx`
               helpers.downloadExport(r.data, XLSX_FILENAME, constants.MIME_TYPES.xlsx)
            } else {
               toast.error(r.message || "Error downloading report");
            }
         })
         .catch(api.catchHandler);
   }

   function Go() {
      if (!vendor) {
         toast.info("Select a valid vendor first to show results");
         return;
      }
      refreshData();
   }

   function onChangeDateTime(date, type) {
      if (!date) {
         toast.info("Please select a valid DateTime")
         return;
      }
      if (type === "endDate") {
         if (Date.parse(date) < Date.parse(searchDates.startDate)) {
            toast.warning("You end date cannot be before your start date")
            return;
         }
         setSearchDates({ ...searchDates, endDate: date })
      } else {
         setSearchDates({ ...searchDates, startDate: date })
      }
   }

   function onPage(pageNumber) {
      setPage({ ...page, number: pageNumber })
   }

   function onSort(field, direction) {
      setSort({ ...sort, field: field, direction: direction })
      setPage({ ...page, number: 1 })
   }

   function refreshData() {
      let payloadParam = location?.state?.type === "showSearch" ? {
         startDate: dateHelpers.formatDateForServer(searchDates.startDate),
         endDate: dateHelpers.formatDateForServer(searchDates.endDate),
         pageNumber: 0,
         pageSize: 1000, //this should suffice as a limit but can be changed 
         sortField: sort.field,
         sortDirection: sort.direction
      } : null;

      if (location.state && location.state.type === "showSearch" && !vendor) return;
      setWorking(true);
      const payload = {
         startDate: dateHelpers.getLookbackByDays(120),
         endDate: dateHelpers.currentDate(),
         pageNumber: page.number,
         pageSize: page.size,
         sortField: sort.field,
         sortDirection: sort.direction
      };
      let vendorParamater = vendorId;
      if (!vendorParamater && location?.state && location?.state?.type === "showSearch" && vendor) vendorParamater = vendor.value;
      api
         .fetch(`Purchasing/PagedVendorInvoices/${vendorParamater}`, payloadParam ? payloadParam : payload)
         .then(r => {
            if (r.data.success) {
               setData(r.data.message.items)
               setTotalPages(_.ceil(r.data.message.totalCount / page.size))
            } else {
               toast.error("Error loading Vendor Invoice information");
               console.error(r.data.message);
            }
         })
         .catch(api.catchHandler)
         .finally(() => setWorking(false));
   }


   if (location.state && location.state.type === "showSearch") {
      return (
         <DesktopContainer screenName={"Vendor Invoices"}>
            <div className='filter-header mb-3'>
               <Row>
                  <Col xs={{ offset: 1, size: 2 }} className={"text-white"} children={<h5 style={{ textAlign: "right" }}>Vendor:</h5>} />
                  <Col xs={4}>
                     <VendorTypeahead
                        value={vendor}
                        onChange={opt => navigate(constants.ENDPOINTS.vendorInvoices, { state: { vendor: opt, type: "showSearch" } })}
                     />
                  </Col>
               </Row>
               <Row className='mt-2'>
                  <Col xs={{ offset: 1, size: 2 }} className={"text-white"} children={<h5 style={{ textAlign: "right" }}>Start Date:</h5>} />
                  <Col xs={8}>
                     <DebounceInput
                        type='Date'
                        value={dateHelpers.formatDateForServer(searchDates.startDate)}
                        onChange={(e) => onChangeDateTime(e.target.value, "startDate")}
                     />
                  </Col>
               </Row>
               <Row className='mt-2'>
                  <Col xs={{ offset: 1, size: 2 }} className={"text-white"} children={<h5 style={{ textAlign: "right" }}>End Date:</h5>} />
                  <Col xs={8}>
                     <DebounceInput
                        type='Date'
                        value={dateHelpers.formatDateForServer(searchDates.endDate)}
                        onChange={(e) => onChangeDateTime(e.target.value, "endDate")}
                     />
                  </Col>
               </Row>
               <Row>
                  <Col xs={{ offset: 9, size: 3 }} className="transparent-background">
                     <Button
                        title="Search Invoices"
                        className="shredButton float-start ms-2"
                        onClick={() => Go()}
                        disabled={working}
                     >
                        Go
                     </Button>
                  </Col>
               </Row>
            </div>
            <ServiceThreadSpinner on={working}>
               {data && data.length > 0
                  ? <VendorInvoiceGrid
                     working={working}
                     data={data}
                     page={page}
                     totalPages={totalPages}
                     sort={sort}
                     exportExcel={exportExcel}
                     hidePager
                  />
                  : <div className="h5 text-center">No Invoice Activity found in this dateRange</div>
               }
            </ServiceThreadSpinner>
         </DesktopContainer>
      );
   } else {
      return (
         <>
            <div>
               <span className="h2 me-2" children="PO Invoices Last 120 Days" />{" "}
               <span className="text-muted" >Vendor {vendorId}</span>
            </div>
            <hr />
            <ServiceThreadSpinner on={working}>
               {data && data.length > 0
                  ? <VendorInvoiceGrid
                     working={working}
                     data={data}
                     page={page}
                     totalPages={totalPages}
                     sort={sort}
                     exportExcel={exportExcel}
                     vendorParam={{ label: vendorName, value: vendorId }}
                  />
                  : <div className="h5 text-center">No Invoice Activity for the last 120 days found</div>
               }
            </ServiceThreadSpinner>
         </>
      );
   }
}